/**
 * La fiche d'un enfant, et ses sous-onglets qui restent.
 *
 * Demandé par Hedi le 6 septembre 2026 : *« pour un enfant quand je clique sur
 * le travail fourni je veux que les sous-tabs "le travail fourni", "Résumés de
 * séances", … restent visibles. Même chose pour les autres liens »*.
 *
 * **Le défaut n'était pas dans le dessin, il était dans le routage.** Les six
 * écrans d'un enfant étaient des routes *sœurs* sous `/parents` : ouvrir l'un
 * remplaçait la page entière, y compris les liens qui menaient aux cinq autres.
 * Le parent devait repasser par la fiche pour aller du travail fourni au
 * journal. Cette route-ci est un **layout** : elle porte la rangée d'onglets et
 * un `<Outlet/>`, si bien que la navigation d'un enfant survit à la navigation
 * *dans* cet enfant.
 *
 * Le 27 septembre 2026, deux onglets en regroupent d'autres, chacun avec sa
 * propre rangée en dessous : « Vue d'ensemble » (Synthèse, Compétences et
 * exercices, Le travail fourni, Résumés de séance — `VueDEnsembleLayout`) et,
 * juste après, « Entraînements » (les cahiers, montés depuis `v2/` —
 * `V2ChildLayout`). L'enfant est choisi ici, par la barre du haut, et plus à
 * l'intérieur des cahiers.
 *
 * Le prénom est affiché ici, une fois, plutôt que par chaque écran : sur un
 * écran de résumés, savoir de qui l'on parle n'est pas un détail — c'est la
 * seule chose que six écrans identiques ne diraient pas.
 */

import { Outlet, useParams } from "react-router-dom";

import { SOUS_LA_VUE_D_ENSEMBLE } from "@/parent/VueDEnsembleLayout";
import { useEnfants } from "@/ui/enfants";
import { SousOnglets } from "@/ui/SousOnglets";

export function ChildLayout() {
  const { childId = "" } = useParams();
  const enfant = useEnfants().find((candidat) => candidat.child_id === childId);
  const base = `/parents/enfant/${childId}`;

  const tabs = [
    {
      to: base,
      label: "Vue d'ensemble",
      end: true,
      aussi: SOUS_LA_VUE_D_ENSEMBLE.map(({ segment }) => `${base}/${segment}`),
    },
    // L'adresse est écrite en toutes lettres et non lue dans `v2/chemins` :
    // la coquille parent n'importe rien de `v2/`, et `App.tsx` monte les deux.
    { to: `${base}/entrainements`, label: "Entraînements" },
    { to: `${base}/prochain`, label: "Ce qui lui sera proposé" },
    { to: `${base}/travail-demande`, label: "Travail demandé" },
    { to: `${base}/classe`, label: "Ce que disent les profs" },
    // Le libellé est **repris mot pour mot** par `ui/planDuSoir` : le plan du
    // soir renvoie ici le parent dont les chapitres sans preuve ont été écartés.
    { to: `${base}/vu-en-classe`, label: "Ce qui a été vu" },
    { to: `${base}/journal`, label: "Son journal" },
    { to: `${base}/echanger`, label: "Poser une question" },
  ];

  return (
    <>
      <SousOnglets tabs={tabs} label={enfant ? enfant.display_name : "Enfant"} />
      <Outlet />
    </>
  );
}
